import { PropertiesSelectors } from '../selectors';

import CurrentProperty from '../model/CurrentProperty';
import Unit from '../model/Unit';
import VrModel from '../model/VrModel';
import ExternalTour from '../model/ExternalTour';
import ContactEmailDeliverability from '../model/ContactEmailDeliverability';
import Freemium from '../model/Freemium';
import PropertyOverview from '../model/PropertyOverview';
import RentGuarantee from '../model/RentGuarantee';

import api from '../utils/api';
import postingUtils from '../utils/postingUtils';

import Constants from '../constants/';

import PropertiesActions from './PropertiesActions';
import { logError } from '@zg-rentals/log-error';

const ERROR = {
  CONNECTION_ABORTED: 'Connection aborted',
  FILE_SIZE_EXCEEDS_MAX: 'file size exceeds maximum',
  UPLOAD_PHOTO_ERROR: 'Error when uploading photo',
};

/*
 * formatMaxFileSizeMsg
 * @param origMsg
 *
 * @origMsg format must be [message] : [filesize_number]
 */
function formatMaxFileSizeMsg(origMsg) {
  const msgArr = origMsg.split(':');
  let msg = msgArr[0];
  const value = msgArr[1].trim() / 1024 / 1024 || 0; // convert to mb
  msg = `${msg}: ${value}MB`;

  return msg;
}

const PostingActions = {
  apartment: {
    amenity: {
      create(propertyAmenity) {
        return (dispatch) =>
          dispatch(api.users.properties.apartmentAmenities.create(propertyAmenity))
            .then((result) => result)
            .catch((err) => Promise.reject(err.message));
      },
      delete(amenityId) {
        return (dispatch) =>
          dispatch(api.users.properties.apartmentAmenities.delete(amenityId))
            .then((result) => result)
            .catch((err) => Promise.reject(err.message));
      },
    },
  },
  floorplan: {
    amenity: {
      create(unitAmenity) {
        return (dispatch) =>
          dispatch(api.users.properties.unitAmenities.create(unitAmenity))
            .then((result) => {
              const { error: errorRMWModel, success } = result;

              if (success === false) {
                return Promise.reject(errorRMWModel);
              }

              return result;
            })
            .catch((errorRMWModel) => {
              logError({
                error: errorRMWModel,
                context: 'PostingActions.floorplan.amenity.create',
              });

              return Promise.reject(errorRMWModel);
            });
      },
      delete(amenityId) {
        return (dispatch) =>
          dispatch(api.users.properties.unitAmenities.delete(amenityId))
            .then((result) => result)
            .catch((err) => Promise.reject(err.message));
      },
    },
    layout: {
      delete(floorPlanId) {
        return (dispatch) =>
          dispatch(api.users.properties.floorPlans.layouts.delete(floorPlanId)).catch((err) =>
            Promise.reject(err.message),
          );
      },
      upload(formData) {
        return (dispatch) =>
          dispatch(api.users.properties.floorPlans.layouts.upload(formData))
            .then((result) => {
              const { error: errorRMWModel, success } = result;

              if (success === false) {
                return Promise.reject(errorRMWModel);
              }
              if (result.hasOwnProperty('code') && result.code === 'ECONNABORTED') {
                // connection aborted
                errorRMWModel.message = ERROR.CONNECTION_ABORTED;

                return Promise.reject(errorRMWModel);
              }

              return result.response;
            })
            .catch((errorRMWModel) => {
              const msg = errorRMWModel.message;

              // is filesize error
              if (errorRMWModel.message.toLowerCase().indexOf(ERROR.FILE_SIZE_EXCEEDS_MAX) > -1) {
                errorRMWModel.message = formatMaxFileSizeMsg(errorRMWModel.message);
              }

              errorRMWModel.message = msg;

              // do not dispatch via NewRelicService. the components will have better context

              return Promise.reject(errorRMWModel);
            });
      },
    },
    photo: {
      caption(photoId, caption) {
        const photoObj = {
          photoId,
          caption,
        };

        return (dispatch) =>
          dispatch(api.users.properties.floorPlans.photos.caption(photoObj)).catch((err) =>
            Promise.reject(err.message),
          );
      },
      delete(photoId) {
        return (dispatch) =>
          dispatch(api.users.properties.floorPlans.photos.delete(photoId)).catch((err) => Promise.reject(err.message));
      },
      upload(formData) {
        return (dispatch) =>
          dispatch(api.users.properties.floorPlans.photos.upload(formData))
            .then((result) => {
              const { error: errorRMWModel, success } = result;

              if (success === false) {
                return Promise.reject(errorRMWModel);
              }
              if (result.hasOwnProperty('code') && result.code === 'ECONNABORTED') {
                // connection aborted
                errorRMWModel.message = ERROR.CONNECTION_ABORTED;

                return Promise.reject(errorRMWModel);
              }

              return result.response;
            })
            .catch((errorRMWModel) => {
              // is filesize error
              if (errorRMWModel.message.toLowerCase().indexOf(ERROR.FILE_SIZE_EXCEEDS_MAX) > -1) {
                errorRMWModel.message = formatMaxFileSizeMsg(errorRMWModel.message);
              }

              // do not dispatch via NewRelicService. the components will have better context

              return Promise.reject(errorRMWModel);
            });
      },
    },
    get(floorplanId) {
      return (dispatch) =>
        dispatch(api.users.properties.floorPlans.get(floorplanId))
          .then((result) => result.response)
          .catch((err) => Promise.reject(err.message));
    },
    edit(formData) {
      return (dispatch, getState) => {
        const mapFormFieldNameToApiFieldName = postingUtils.mapFormDataToApi(`floorplans.${formData.fieldName}`);

        const { listingAlias } = PropertiesSelectors.makeCurrentProperty(getState());

        const dataToSave = {
          fieldName: mapFormFieldNameToApiFieldName,
          [mapFormFieldNameToApiFieldName]: formData.value,
          floorPlanId: formData.floorPlanId,
        };

        return dispatch(api.users.properties.floorPlans.edit(dataToSave))
          .then((result) => {
            if (result.success === false) {
              return Promise.reject(result.error);
            }

            return result.response;
          })
          .catch((err) => {
            logError({
              error: err,
              context: `PostingActions.floorplan.edit: listingAlias: ${listingAlias} ${JSON.stringify(formData)}`,
            });

            return Promise.reject(err.message);
          });
      };
    },
  },
  property: {
    amenity: {
      create(propertyAmenity, componentGroupName = '') {
        return (dispatch) =>
          dispatch(api.users.properties.amenities.create(propertyAmenity))
            .then((result) => {
              if (result.success === false) {
                return Promise.reject(result.error);
              }

              return result;
            })
            .catch((errorRMWModel) => {
              logError({
                error: errorRMWModel,
                context: 'PostingActions.property.amenity.create',
              });

              return Promise.reject(errorRMWModel);
            });
      },
      delete(amenityId) {
        return (dispatch) =>
          dispatch(api.users.properties.amenities.delete(amenityId))
            .then((result) => result)
            .catch((err) => Promise.reject(err.message));
      },
      update(data) {
        return (dispatch) =>
          dispatch(api.users.properties.amenities.update(data))
            .then((result) => {
              if (result.success === false) {
                return Promise.reject(result.error);
              }

              return result;
            })
            .catch((errorRMWModel) => {
              logError({
                error: errorRMWModel,
                context: 'PostingActions.property.amenity.update',
              });

              return Promise.reject(errorRMWModel);
            });
      },
    },
    photo: {
      caption(photoId, caption) {
        const photoObj = {
          photoId,
          caption,
        };

        return (dispatch) =>
          dispatch(api.users.properties.photos.caption(photoObj))
            .then((result) => {
              const { error: errorRMWModel, success } = result;

              if (success === false) {
                return Promise.reject(errorRMWModel);
              }

              return result.response;
            })
            .catch((errorRMWModel) => Promise.reject(errorRMWModel));
      },
      delete(photoId) {
        return (dispatch) =>
          dispatch(api.users.properties.photos.delete(photoId))
            .then((result) => {
              const { error: errorRMWModel, success } = result;

              if (success === false) {
                return Promise.reject(errorRMWModel);
              }

              return result.response;
            })
            .catch((errorRMWModel) => Promise.reject(errorRMWModel));
      },
      sort(dataToSave) {
        return (dispatch) => {
          const photoSortObj = {
            propertyId: dataToSave.propertyId,
            photosArray: dataToSave.photosArray,
          };

          return dispatch(api.users.properties.photos.sort(photoSortObj))
            .then((result) => {
              const { error: errorRMWModel, success } = result;

              if (success === false) {
                return Promise.reject(errorRMWModel);
              }

              return result.response;
            })
            .catch((errorRMWModel) => {
              logError({
                error: errorRMWModel,
                context: 'PostingActions.property.photo.sort',
              });

              return Promise.reject(errorRMWModel);
            });
        };
      },
      upload(formData) {
        return (dispatch) =>
          dispatch(api.users.properties.photos.upload(formData))
            .then((result) => {
              const { error: errorRMWModel, success } = result;

              if (success === false) {
                return Promise.reject(errorRMWModel);
              }
              if (result.hasOwnProperty('code') && result.code === 'ECONNABORTED') {
                // connection aborted
                return Promise.reject({ message: ERROR.CONNECTION_ABORTED });
              }

              return result.response;
            })
            .catch((errorRMWModel) => {
              let errorMessage;

              if (errorRMWModel) {
                errorMessage =
                  errorRMWModel.message ||
                  (typeof errorRMWModel === 'string' ? errorRMWModel : ERROR.UPLOAD_PHOTO_ERROR);
              } else {
                errorMessage = ERROR.UPLOAD_PHOTO_ERROR;
              }

              // is filesize error
              if (errorMessage.toLowerCase().indexOf(ERROR.FILE_SIZE_EXCEEDS_MAX) > -1) {
                errorMessage = formatMaxFileSizeMsg(errorMessage);
              }

              return Promise.reject(errorMessage);
            });
      },

      replace(params) {
        return (dispatch) =>
          dispatch(api.users.properties.photos.replace(params))
            .then((result) => {
              if (result.success === false) {
                return Promise.reject(result.error);
              }
              return result.response;
            })
            .catch((err) => {
              err = {
                // sometimes error response does not have message property
                message: err.message || err,
              };

              return Promise.reject(err.message);
            });
      },
    },
    activate(propertyId, extending) {
      const daysToExtend = extending ? 30 : null;

      return (dispatch) =>
        dispatch(api.users.properties.activate(propertyId, daysToExtend))
          .then((result) => {
            const { error: errorRMWModel, success } = result;

            if (success === false) {
              return Promise.reject(errorRMWModel);
            }
            return result;
          })
          .catch((errorRMWModel) => Promise.reject(errorRMWModel));
    },
    deactivate(propertyId, reason = null, comment = null) {
      return (dispatch) =>
        dispatch(api.users.properties.deactivate(propertyId, reason, comment))
          .then((result) => {
            if (result.success === false) {
              return Promise.reject(result.error);
            }
            return result;
          })
          .catch((errorRMWModel) => {
            logError({
              error: errorRMWModel,
              context: 'PostingActions.property.deactivate',
            });

            return Promise.reject(errorRMWModel);
          });
    },
    deactivateFeedback(feedbackId, reason, comment) {
      return (dispatch) =>
        dispatch(api.users.properties.deactivateFeedback(feedbackId, reason, comment))
          .then((result) => {
            if (result.success === false) {
              return Promise.reject(result.error);
            }
            return result;
          })
          .catch((errorRMWModel) => {
            logError({
              error: errorRMWModel,
              context: 'PostingActions.property.deactivateFeedback',
            });

            return Promise.reject(errorRMWModel);
          });
    },
    create(formData) {
      return (dispatch) => {
        const dataForApi = postingUtils.transformPropertyListingTypeForApi(formData);

        return dispatch(api.users.properties.create(dataForApi))
          .then((result) => {
            const { error: errorRMWModel, success } = result;

            if (success === false) {
              return Promise.reject(errorRMWModel);
            }
            const { propertyId } = result.response;

            // TODO: get details with listingAlias
            return dispatch(PostingActions.property.getDetails(propertyId));
          })
          .catch((errorRMWModel) => {
            logError({
              error: errorRMWModel,
              context: 'PostingActions.property.create',
            });

            return Promise.reject(errorRMWModel);
          });
      };
    },
    delete(propertyId, reason) {
      return (dispatch) =>
        dispatch(api.users.properties.delete(propertyId, reason))
          .then((result) => result)
          .catch((err) => Promise.reject(err.message));
    },
    // archive property
    archive(propertyId) {
      return (dispatch) =>
        dispatch(api.users.properties.archive(propertyId))
          .then((result) => {
            if (result.success) {
              // if archive is called -> safely say that property could be unarchived.
              // set the current property in store with canBeArchived as false
              dispatch(PropertiesActions.updateCurrentProperty({ canBeArchived: false }));
            }
            return result;
          })
          .catch((err) => Promise.reject(err.message));
    },
    // unarchive property
    unArchive(propertyId) {
      return (dispatch) =>
        dispatch(api.users.properties.unArchive(propertyId))
          .then((result) => {
            if (result.success) {
              // if unarchive is called -> safely say that property was able to be archived.
              // set the current property in store with canBeArchived as true
              dispatch(PropertiesActions.updateCurrentProperty({ canBeArchived: true }));
            }
            return result;
          })
          .catch((err) => Promise.reject(err.message));
    },
    markAcknowledgedLandlordMessage(actionId) {
      return (dispatch) =>
        dispatch(api.users.properties.markAcknowledgedLandlordMessage(actionId))
          .then((response) => {
            if (response.success && response.response) {
              return response.response;
            }
            return Promise.reject(response.error);
          })
          .catch((errorRMWModel) => {
            logError({
              error: errorRMWModel,
              context: 'PostingActions.markAcknowledgedLandlordMessage',
            });

            return Promise.reject(errorRMWModel);
          });
    },
    // add boolean for restrictions
    getDetails(propertyId) {
      return this.getDetailsInternal(api.users.properties.getDetails(propertyId));
    },
    getDetailsInternal(dispatchedApi) {
      return (dispatch) =>
        dispatch(dispatchedApi)
          .then((result) => {
            const { error: errorRMWModel, success, response } = result;
            let dataForForm = {};
            let mergedListingAndTransformedData = {};

            if (success) {
              dataForForm = postingUtils.transformDatafromApi(response);
              dataForForm.propertyId = dataForForm.listingId;

              const freemiumData = response?.freemium || {};

              mergedListingAndTransformedData = {
                ...dataForForm,
                freemium: freemiumData,
              };

              /**
               * there is an issue where CurrentProperty.rentalProtection gets reset.
               * this is because we merge all of the properties back into CurrentProperty

               *
               * a fix on the FE is to remove the rentGuarantee key from the api response
               * and set the value onto the rentalProtection key
               *
               * the long term solution would be for the BE to return rentalProtection key from the api response
               */
              mergedListingAndTransformedData.rentalProtection = mergedListingAndTransformedData.rentGuarantee;

              delete mergedListingAndTransformedData.rentGuarantee;

              const currentPropertyModel = CurrentProperty(mergedListingAndTransformedData);

              dispatch(PropertiesActions.updateCurrentProperty(currentPropertyModel));
            } else {
              // error
              return Promise.reject({
                ...errorRMWModel,
                message: 'could not load the property details',
              });
            }

            return mergedListingAndTransformedData;
          })
          .catch((errorRMWModel) => {
            logError({
              error: errorRMWModel,
              context: 'PostingActions.getDetails',
            });

            return Promise.reject(errorRMWModel);
          });
    },
    getDetailsOverview(listingAlias, includeRestrictions = false) {
      return (dispatch, getState) =>
        dispatch(api.users.properties.getDetailsOverview(listingAlias, includeRestrictions))
          .then((result) => {
            const { error: errorRMWModel, success, response } = result;

            if (success === false) {
              return Promise.reject(errorRMWModel);
            }

            const currentProperty = PropertiesSelectors.makeCurrentProperty(getState());

            const propertyOverview = PropertyOverview(response);

            dispatch({
              type: Constants.UPDATE_PROPERTY_OVERVIEW,
              payload: propertyOverview,
            });

            dispatch(
              PropertiesActions.updateCurrentProperty({
                contactEmailDeliverability: ContactEmailDeliverability(response.contactEmailDeliverability),
                active: response.status === 'LISTED FOR RENT',
                freemium: Freemium(response.freemium),
                street: response.address.street,
                city: response.address.city,
                state: response.address.state,
                listingTypeCode: response.listingType.code,
                zip: response.address.zip,
                unit: response.address.unit,
                isMultifamily: response.isMultifamily,
                isArchived: response.isArchived,
                restrictions: {
                  ...currentProperty.restrictions,
                  ...response?.restrictions,
                  isLeasesRestricted: response.isLeasesRestricted,
                  isLeaseBuilderRestricted: response.isLeaseBuilderRestricted,
                },
                listingAlias: response.listingAlias,
                listingAvailability: response.listingAvailability || {},
                rentalProtection: RentGuarantee(response.rentGuarantee),
                updateActiveStatus: response.updateActiveStatus,
              }),
            );

            return response;
          })
          .catch((errorRMWModel) => {
            logError({
              error: errorRMWModel,
              context: 'PostingActions.getRestrictions',
            });

            return Promise.reject(errorRMWModel);
          });
    },
    getRestrictions(listingAlias) {
      return (dispatch) =>
        dispatch(api.users.properties.getRestrictions(listingAlias))
          .then((result) => {
            const { error: errorRMWModel, success, response } = result;

            if (success === false) {
              return Promise.reject(errorRMWModel);
            }

            dispatch(PropertiesActions.updateCurrentPropertyRestrictions(response));

            return response;
          })
          .catch((errorRMWModel) => {
            logError({
              error: errorRMWModel,
              context: 'PostingActions.getRestrictions',
            });

            return Promise.reject(errorRMWModel);
          });
    },
    getStatus(propertyId) {
      return (dispatch) =>
        dispatch(api.users.properties.getStatus(propertyId))
          .then((result) => {
            const { error: errorRMWModel, success, response } = result;

            if (success === false) {
              return Promise.reject(errorRMWModel);
            }

            // sync with current property
            const { expirationDate, dateLastActivated } = response;

            dispatch(
              PropertiesActions.updateCurrentProperty({
                dateLastActivated,
                expirationDate,
              }),
            );

            return response;
          })
          .catch((errorRMWModel) => {
            logError({
              error: errorRMWModel,
              context: 'PostingActions.getStatus',
            });

            return Promise.reject(errorRMWModel);
          });
    },
    edit(formData, { componentGroupName = '', preserveFieldName = false, reserveErrorObject = false } = {}) {
      return (dispatch) => {
        let dataToSave;

        if (formData.fieldName === 'isApplicationsAccepted') {
          formData.value = formData.value || false;
        }

        if (preserveFieldName === false) {
          const mapFormFieldNameToApiFieldName = postingUtils.mapFormDataToApi(formData.fieldName);

          if (mapFormFieldNameToApiFieldName === undefined) {
            console.error('ERROR: field is not found in mapFormDataToApi', formData.fieldName);

            return Promise.reject();
          }

          dataToSave = {
            fieldName: mapFormFieldNameToApiFieldName,
            [mapFormFieldNameToApiFieldName]: formData.value,
            ...(formData.additionalParams && { additionalParams: formData.additionalParams }),
            propertyId: formData.propertyId,
          };
        } else {
          const { fieldName, additionalParams } = formData;

          dataToSave = {
            fieldName,
            [fieldName]: formData.value,
            ...(additionalParams && { additionalParams }),
            propertyId: formData.propertyId,
          };
        }

        // Handle an issue on the API side when an empty value is provided.
        if (formData.fieldName === 'contactEmail' && formData.value === '') {
          return Promise.reject('Please enter a valid email address.');
        }

        return dispatch(api.users.properties.edit(dataToSave))
          .then((result) => {
            if (result.success === false) {
              return Promise.reject(result.error);
            }

            return result;
          })
          .catch((err) => {
            return Promise.reject(reserveErrorObject ? err : err.message);
          });
      };
    },
    pets(dataToSave) {
      return (dispatch) =>
        dispatch(api.users.properties.pets(dataToSave))
          .then((result) => {
            if (result.success === false) {
              return Promise.reject(result.error);
            }

            return result;
          })
          .catch((err) => Promise.reject(err.message));
    },
    modularListingProgress(params) {
      return (dispatch, getState) => {
        if (getState?.()?.properties?.currentProperty?.isFeed) {
          return;
        }

        return dispatch(api.users.properties.modularListingProgress(params))
          .then((result) => {
            const { error: errorRMWModel, success, response } = result;

            if (!success) {
              return Promise.reject({
                ...errorRMWModel,
                message: 'Could not save progress.  Please try again.',
              });
            }

            dispatch(
              PropertiesActions.updateCurrentProperty({
                modularListingProgress: response,
              }),
            );

            return response;
          })
          .catch((errorRMWModel) => {
            logError({
              error: errorRMWModel,
              context: 'PostingActions.modularListingProgress',
            });

            return Promise.reject(errorRMWModel);
          });
      };
    },
    createNewFloorplan(propertyId) {
      return (dispatch) =>
        dispatch(api.users.properties.floorPlans.create(propertyId))
          .then((result) => result.response)
          .catch((err) => Promise.reject(err.message));
    },
    unit: {
      create(floorPlanId, params) {
        const mappedParams = Object.keys(params).reduce((acc, key) => {
          const backendKey = postingUtils.mapFormDataToApi(`floorplans.${key}`);
          acc[backendKey] = params[key];
          return acc;
        }, {});
        return (dispatch) =>
          dispatch(api.users.properties.floorPlans.units.createV2(floorPlanId, mappedParams)).then((result) => {
            if (result.error) {
              return Promise.reject(result);
            }
            return Unit(result.response);
          });
      },
      edit(unitId, params) {
        const mappedParams = Object.keys(params).reduce((acc, key) => {
          const backendKey = postingUtils.mapFormDataToApi(`floorplans.${key}`);
          acc[backendKey] = params[key];
          return acc;
        }, {});
        return (dispatch) =>
          dispatch(api.users.properties.floorPlans.units.edit(unitId, mappedParams)).then((result) => {
            if (result.error) {
              return Promise.reject(result);
            }
            return result;
          });
      },
      get(unitId) {
        return (dispatch) =>
          dispatch(api.users.properties.floorPlans.get(unitId))
            .then((result) => Unit(result.response))
            .catch((err) => Promise.reject(err.message));
      },
    },
    vrModel: {
      getAll(encodedAlias) {
        return (dispatch) =>
          dispatch(api.users.properties.vrModel.getAll(encodedAlias))
            .then((result) => {
              const { error: errorRMWModel, success } = result;
              if (success === false) {
                return Promise.reject(errorRMWModel);
              }

              return result.response.vrModels.map((vrModel) => VrModel(vrModel));
            })
            .catch((err) => Promise.reject(err.message));
      },
      associateModel(vrModelId, encodedAlias, shouldAssociate, encodedFloorplanKey) {
        return (dispatch) =>
          dispatch(
            api.users.properties.vrModel.associateModel(vrModelId, encodedAlias, shouldAssociate, encodedFloorplanKey),
          )
            .then((result) => {
              const { error: errorRMWModel, success } = result;
              if (success === false) {
                return Promise.reject(errorRMWModel);
              }

              return VrModel(result.response);
            })
            .catch((err) => Promise.reject(err.message));
      },
      associateListing(vrModelId, encodedAlias, shouldAssociate) {
        return (dispatch) =>
          dispatch(api.users.properties.vrModel.associateListing(vrModelId, encodedAlias, shouldAssociate))
            .then((result) => {
              const { error: errorRMWModel, success } = result;
              if (success === false) {
                return Promise.reject(errorRMWModel);
              }

              return VrModel(result.response);
            })
            .catch((err) => Promise.reject(err.message));
      },
      publishModel(vrModelId, encodedAlias, encodedFloorplanKey) {
        return (dispatch) =>
          dispatch(api.users.properties.vrModel.publishModel(vrModelId, encodedAlias, encodedFloorplanKey))
            .then((result) => {
              const { error: errorRMWModel, success } = result;
              if (success === false) {
                return Promise.reject(errorRMWModel);
              }

              return VrModel(result.response);
            })
            .catch((err) => Promise.reject(err.message));
      },
      publishListing(vrModelId, encodedAlias) {
        return (dispatch) =>
          dispatch(api.users.properties.vrModel.publishListing(vrModelId, encodedAlias))
            .then((result) => {
              const { error: errorRMWModel, success } = result;
              if (success === false) {
                return Promise.reject(errorRMWModel);
              }

              return VrModel(result.response);
            })
            .catch((err) => Promise.reject(err.message));
      },
    },
    externalTour: {
      associateModel(externalTour, encodedAlias, shouldAssociate, encodedFloorplanKey) {
        return (dispatch) =>
          dispatch(
            api.users.properties.externalTour.associateModel(
              externalTour,
              encodedAlias,
              shouldAssociate,
              encodedFloorplanKey,
            ),
          )
            .then((result) => {
              const { error: errorRMWModel, success } = result;
              if (success === false) {
                return Promise.reject(errorRMWModel);
              }

              return ExternalTour(result.response);
            })
            .catch((err) => Promise.reject(err.message));
      },
      associateListing(externalTour, encodedAlias, shouldAssociate) {
        return (dispatch) =>
          dispatch(api.users.properties.externalTour.associateListing(externalTour, encodedAlias, shouldAssociate))
            .then((result) => {
              const { error: errorRMWModel, success } = result;
              if (success === false) {
                return Promise.reject(errorRMWModel);
              }

              return ExternalTour(result.response);
            })
            .catch((err) => Promise.reject(err.message));
      },
      publishModel(externalTour, encodedAlias, encodedFloorplanKey) {
        return (dispatch) =>
          dispatch(api.users.properties.externalTour.publishModel(externalTour, encodedAlias, encodedFloorplanKey))
            .then((result) => {
              const { error: errorRMWModel, success } = result;
              if (success === false) {
                return Promise.reject(errorRMWModel);
              }

              return ExternalTour(result.response);
            })
            .catch((err) => Promise.reject(err.message));
      },
      publishListing(externalTour, encodedAlias) {
        return (dispatch) =>
          dispatch(api.users.properties.externalTour.publishListing(externalTour, encodedAlias))
            .then((result) => {
              const { error: errorRMWModel, success } = result;
              if (success === false) {
                return Promise.reject(errorRMWModel);
              }

              return ExternalTour(result.response);
            })
            .catch((err) => Promise.reject(err.message));
      },
    },
    createNewUnit(floorPlanId) {
      return (dispatch) =>
        dispatch(api.users.properties.floorPlans.units.create(floorPlanId))
          .then((result) => result.response)
          .catch((err) => Promise.reject(err.message));
    },
    removeFloorplan(floorplanId) {
      return (dispatch) =>
        dispatch(api.users.properties.floorPlans.delete(floorplanId))
          .then((result) => {
            const { error: errorRMWModel, success } = result;

            if (success === false) {
              return Promise.reject(errorRMWModel);
            }

            return result.response;
          })
          .catch((errorRMWModel) => {
            logError({
              error: errorRMWModel,
              context: 'PostingActions.property.removeFloorplan',
            });

            return Promise.reject(errorRMWModel);
          });
    },
    syndication(propertyId) {
      return (dispatch) =>
        dispatch(api.users.properties.syndication(propertyId))
          .then((result) => {
            if (result.success === false) {
              return Promise.reject(result.error);
            }

            return result.response;
          })
          .catch((err) => {
            const msg = err.message;
            return Promise.reject(msg);
          });
    },
    uploadLogo(formData) {
      return (dispatch) =>
        dispatch(api.users.properties.logos.upload(formData))
          .then((result) => {
            if (result.success === false) {
              return Promise.reject(result.error);
            }
            if (result.hasOwnProperty('code') && result.code === 'ECONNABORTED') {
              // connection aborted
              return Promise.reject({ message: ERROR.CONNECTION_ABORTED });
            }

            return result.response;
          })
          .catch((err) => {
            let msg = err.message;

            // is filesize error
            if (msg.toLowerCase().indexOf(ERROR.FILE_SIZE_EXCEEDS_MAX) > -1) {
              msg = formatMaxFileSizeMsg(err.message);
            }

            return Promise.reject(msg);
          });
    },
    removeLogo(propertyId) {
      return (dispatch) =>
        dispatch(api.users.properties.logos.delete(propertyId)).catch((err) => Promise.reject(err.message));
    },
    confirmVerification(contactData) {
      return (dispatch) =>
        dispatch(api.users.contacts.verifier.confirm(contactData))
          .then((result) => result)
          .catch((err) => Promise.reject(err.message));
    },
    confirmEmailVerification(contactData) {
      return (dispatch) =>
        dispatch(api.users.contacts.verifier.confirmEmail(contactData))
          .then((result) => result)
          .catch((err) => Promise.reject(err.message));
    },
    requestVerification(contactData) {
      return (dispatch) =>
        dispatch(api.users.contacts.verifier.send(contactData))
          .then((result) => result)
          .catch((err) => Promise.reject(err.message));
    },
    convertFloorplanOrUnitToNucleus(modelId, unit) {
      return (dispatch) =>
        dispatch(api.users.properties.convertFloorplanOrUnitToNucleus(modelId, unit))
          .then((result) => {
            if (result.success) {
              return result.response;
            }

            return Promise.reject(result.error);
          })
          .catch((err) => Promise.reject(err));
    },
  },
};

export default PostingActions;
